import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import { useForm, useFormState } from 'react-final-form';
import {
  AutocompleteInput,
  ReferenceInput,
  useDataProvider,
  useTranslate,
} from 'react-admin';
import {
  Card,
  CardContent,
  Chip,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AllInclusiveIcon from '@material-ui/icons/AllInclusive';
import LooksOneIcon from '@material-ui/icons/LooksOne';

const useSelectedContractCardStyles = makeStyles((theme) => {
  return {
    root: {
      position: 'relative',
      marginBottom: theme.spacing(2),
      maxWidth: '15rem',
      // Lets the card stretch to match the height of its sibling (the
      // cleaner card) when both sit in a flex row with align-items:
      // stretch — see ContractAndCleanerSelectBlock in AffectationForm.js.
      height: '100%',
      boxSizing: 'border-box',
      // Softens the placeholder <-> filled-card transition (border colour,
      // height as content changes) within the card's own lifetime. Doesn't
      // (can't, with a plain CSS transition) animate the autocomplete <->
      // card swap itself — that's a full component/DOM-node change, not a
      // style change on a persistent element; a real cross-fade there would
      // need an animation library (react-transition-group or similar),
      // which this repo doesn't have.
      transition: 'all 0.15s',
      // Neutral fallback border for an unrecognized/loading contract type —
      // overridden below (rootRécurrent/rootPonctuel) by the matching shift
      // colour so the whole card's outline reflects the selected type, not
      // just the chip.
      border: `1px solid ${theme.palette.divider}`,
    },
    // Card-wide border variants, keyed the same way as the chip colour
    // classes below (contract type, normalized) — same oklch `-border`
    // values as the chip so the card outline and the chip agree.
    rootRécurrent: {
      border: '2px solid oklch(0.30 0.12 250)',
    },
    rootPonctuel: {
      border: '2px solid oklch(0.45 0.13 240)',
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(0.5),
    },
    accountName: {
      fontWeight: 500,
      paddingRight: theme.spacing(4),
    },
    address: {
      color: theme.palette.text.secondary,
    },
    chip: {
      marginTop: theme.spacing(1),
    },
    // Shift nature palette, ported verbatim from cleany-app-v2
    // (src/styles.css, `--shift-permanent`/`--shift-ponctuel` + `-border`
    // custom properties, ADR 0005 §D39) so the contract-type chip reads
    // consistently with the current front's shift colours. `oklch()` is
    // supported by all evergreen browsers (Chrome/Edge 111+, 2023), which
    // covers this repo's internal-user target.
    récurrent: {
      backgroundColor: 'oklch(0.40 0.12 250)',
      border: '1px solid oklch(0.30 0.12 250)',
      color: '#fff',
    },
    ponctuel: {
      backgroundColor: 'oklch(0.55 0.13 240)',
      border: '1px solid oklch(0.45 0.13 240)',
      color: '#fff',
    },
    date: {
      marginTop: theme.spacing(0.5),
    },
    placeholder: {
      borderStyle: 'dashed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    placeholderText: {
      color: theme.palette.text.disabled,
      fontStyle: 'italic',
      textAlign: 'center',
    },
  };
});

// Contract type (normalized) -> the Card-root border class name above.
const CONTRACT_TYPE_ROOT_BORDER_CLASS = {
  récurrent: 'rootRécurrent',
  ponctuel: 'rootPonctuel',
};

// Contract type -> icon, reusing the exact icon choices already used for the
// (affectation) type field: AllInclusive for Permanent/Récurrent (ongoing,
// repeating), LooksOne for Ponctuel (one-off) — see
// components/field/AffectationTypeField.js#ContractTypeIcon.
const CONTRACT_TYPE_ICON = {
  récurrent: AllInclusiveIcon,
  ponctuel: LooksOneIcon,
};

// The `type` picklist comes from Salesforce (opportunity.R_currence__c, see
// care_v1.2 sql/views.sql care.contract_core) via PostgREST/JSON — normalize
// (trim + NFC + case-fold) before matching so a stray trailing space or an
// NFD-decomposed accent (é as e + combining acute, vs the NFC é literal used
// in this source file) doesn't silently fall through to the default styling.
const normalizeContractType = (type) => {
  return (type || '')
    .normalize('NFC')
    .trim()
    .toLowerCase();
};

const getContractTypeIcon = (type) => {
  return CONTRACT_TYPE_ICON[normalizeContractType(type)] || null;
};

// Read-only recap of the selected contract, shown instead of the autocomplete
// once a `contract_id` is picked (see `showSelectedAsCard`). Fetches the
// contract record itself since the form only holds the id — mirrors the
// data-fetch pattern of DocumentCompletenessCard (useDataProvider + useEffect).
const SelectedContractCard = ({ contractId, onReset }) => {
  const classes = useSelectedContractCardStyles();
  const dataProvider = useDataProvider();
  const [contract, setContract] = useState(null);

  useEffect(() => {
    let cancelled = false;
    dataProvider
      .getOne('contract', { id: contractId })
      .then(({ data }) => {
        if (!cancelled) {
          setContract(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContract(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contractId, dataProvider]);

  const normalizedType = contract ? normalizeContractType(contract.type) : '';
  const TypeIcon = contract ? getContractTypeIcon(contract.type) : null;
  const rootBorderClass =
    classes[CONTRACT_TYPE_ROOT_BORDER_CLASS[normalizedType]];

  return (
    <Card variant="outlined" className={clsx(classes.root, rootBorderClass)}>
      <IconButton
        size="small"
        className={classes.closeButton}
        onClick={onReset}
        aria-label="Changer de contrat"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <CardContent>
        <Typography variant="body1" className={classes.accountName}>
          {contract ? contract.account_name : '…'}
        </Typography>
        <Typography variant="body2" className={classes.address}>
          {contract ? contract.site_address : ''}
        </Typography>
        {contract && (
          <Chip
            size="small"
            color="primary"
            className={clsx(classes.chip, classes[normalizedType])}
            icon={TypeIcon ? <TypeIcon /> : undefined}
            label={contract.type}
          />
        )}
        {contract && contract.effective_date && (
          <Typography variant="body2" className={classes.date}>
            Début : {contract.effective_date}
          </Typography>
        )}
        {contract && contract.end_date && (
          <Typography variant="body2" className={classes.date}>
            Fin : {contract.end_date}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

SelectedContractCard.propTypes = {
  contractId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onReset: PropTypes.func.isRequired,
};

// Sober placeholder for the "top" slot (see `slot` prop) when nothing is
// selected yet — same card footprint (width/height) as SelectedContractCard,
// dashed border, so the two-slot row (see ContractAndCleanerSelectBlock in
// AffectationForm.js) never has a visually broken/empty cell while the
// actual autocomplete has moved down to the row below.
const EmptyContractCardPlaceholder = () => {
  const classes = useSelectedContractCardStyles();
  return (
    <Card
      variant="outlined"
      className={clsx(classes.root, classes.placeholder)}
    >
      <Typography variant="body2" className={classes.placeholderText}>
        Aucun contrat sélectionné
      </Typography>
    </Card>
  );
};

export const optionRenderer = (choice, translate) => {
  if (!choice) {
    return '';
  }
  if (!choice.account_name) {
    return translate('components.form.contract_input.all_contract');
  }

  return `${choice.account_name} - ${choice.site_address} - ${choice.type} - ${choice.effective_date}`;
};

// Restricts the choice list to contracts that are either an active recurring
// contract, or not yet started (future `start_date`) — i.e. accounts that are
// or will soon be relevant when creating an affectation. Built as a raw
// PostgREST `or(...)` expression (see the `or` operator in
// ra-data-postgrest.js), since react-admin's ReferenceInput filter only ANDs
// keys together and cannot express this OR natively.
export const buildRecurringOrUpcomingFilter = () => {
  const today = moment().format('YYYY-MM-DD');
  return {
    [`or(and(type.eq.Récurrent,active.is.true),start_date.gt.${today})`]: true,
  };
};

const ContractAutocompleteInput = ({
  affectationIds,
  bottomSlotStyle,
  emptyMessage,
  fullWidth,
  helperText,
  onlyRecurringOrUpcoming,
  resource,
  showSelectedAsCard,
  slot,
  validate,
  ...props
}) => {
  const translate = useTranslate();
  const form = useForm();
  const { values } = useFormState();

  const filter = {
    ...(affectationIds && affectationIds.length
      ? { 'in(id)': affectationIds }
      : {}),
    ...(onlyRecurringOrUpcoming ? buildRecurringOrUpcomingFilter() : {}),
  };

  const isSelected = showSelectedAsCard && Boolean(values.contract_id);

  const card = (
    <SelectedContractCard
      contractId={values.contract_id}
      onReset={() => {
        form.change('contract_id', null);
      }}
    />
  );

  const autocomplete = (
    <ReferenceInput
      {...props}
      filterToQuery={
        /* istanbul ignore next */
        (searchText) => {
          return { account_name: searchText };
        }
      }
      perPage={100}
      source="contract_id"
      sort={{
        field: 'type, start_date',
        order: 'DESC, DESC',
      }}
      reference="contract"
      resource={resource}
      validate={validate}
      allowEmpty
      emptyText="components.form.contract_input.all_contract"
      filter={filter}
    >
      <AutocompleteInput
        optionText={(choice) => {
          return optionRenderer(choice, translate);
        }}
        options={{
          suggestionsContainerProps: {
            /* XXX: no other way to give style to the AutocompleList. react-admin not passing classes to AutocompleList in AutocompleInput */
            style: { zIndex: 2000 },
          },
          placeholder: emptyMessage,
          fullWidth,
        }}
        helperText={helperText}
      />
    </ReferenceInput>
  );

  // Split rendering: the caller (ContractAndCleanerSelectBlock in
  // AffectationForm.js) renders this component twice, once per slot, to
  // build a fixed "results" row (top: card or placeholder, always present)
  // above a "still to fill" row (bottom: the active autocompletes for
  // whichever slots aren't picked yet). Other callers don't pass `slot`, so
  // they keep the single-spot bascule behaviour (card replaces the
  // autocomplete in place) unchanged.
  if (slot === 'top') {
    return isSelected ? card : <EmptyContractCardPlaceholder />;
  }
  if (slot === 'bottom') {
    // Once selected, render nothing at all — not even an empty wrapper div.
    // A caller-provided `bottomSlotStyle` (width cap, etc.) applied
    // unconditionally in the parent (e.g. a <div style={...}> around this
    // component) would still count as a flex item in the "still to fill"
    // row even once empty, breaking justify-content: flex-start for
    // whichever input remains (it would sit after this invisible spacer,
    // offset by the row's gap, instead of flush left). Owning the wrapper
    // here — only emitted alongside real content — avoids that.
    if (isSelected) {
      return null;
    }
    return bottomSlotStyle ? (
      <div style={bottomSlotStyle}>{autocomplete}</div>
    ) : (
      autocomplete
    );
  }

  // Opt-in: once a contract is picked, swap the autocomplete for a read-only
  // recap card (see SelectedContractCard) instead of leaving the input filled
  // with its rendered label. Other callers (CleanerContractsAutocompleteInput,
  // ProviderInvoice/serviceCertificate filters) don't pass this prop, so they
  // keep the plain-autocomplete behaviour unchanged.
  if (isSelected) {
    return card;
  }

  return autocomplete;
};

ContractAutocompleteInput.propTypes = {
  affectationIds: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  bottomSlotStyle: PropTypes.object,
  emptyMessage: PropTypes.string,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.bool,
  onlyRecurringOrUpcoming: PropTypes.bool,
  resource: PropTypes.string,
  showSelectedAsCard: PropTypes.bool,
  slot: PropTypes.oneOf(['top', 'bottom']),
  validate: PropTypes.func,
};

ContractAutocompleteInput.defaultProps = {
  affectationIds: [],
  bottomSlotStyle: undefined,
  emptyMessage: '',
  fullWidth: true,
  helperText: undefined,
  onlyRecurringOrUpcoming: false,
  resource: '',
  showSelectedAsCard: false,
  slot: undefined,
  validate: /* istanbul ignore next */ () => {},
};

export default ContractAutocompleteInput;
